import React from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/seo/SEO';
import { FileText, Share2, Download, Phone } from 'lucide-react';
import ContactForm from '../components/forms/ContactForm';

// Mock product data (would come from API/database in real app)
const product = {
  id: 1,
  category: 'tsl',
  categoryName: 'Трансформаторы ТСЛ',
  name: 'ТСЛ-1000/10/0,4',
  description:
    'Трансформатор силовой с литой изоляцией обмоток ТСЛ-1000/10/0,4 мощностью 1000 кВА предназначен для преобразования электроэнергии в сетях энергосистем и потребителей электроэнергии в условиях внутренней установки.',
  features: {
    Мощность: '1000 кВА',
    'Номинальное напряжение ВН': '10 кВ',
    'Номинальное напряжение НН': '0,4 кВ',
    'Схема и группа соединения обмоток': 'Д/Ун-11',
    'Потери холостого хода': '2100 Вт',
    'Потери короткого замыкания': '10500 Вт',
    'Напряжение короткого замыкания': '6%',
    'Ток холостого хода': '0,8%',
    Вес: '2450 кг',
    'Габариты (Д×Ш×В)': '1700×950×1850 мм',
  },
  advantages: [
    'Пожаробезопасность и экологичность',
    'Снижение расходов на эксплуатацию на 5-10%',
    'Минимальные затраты на монтаж и обслуживание',
    'Компактные размеры по сравнению с масляными трансформаторами',
    'Длительный срок службы – более 30 лет',
    'Повышенная стойкость к перегрузкам и коротким замыканиям',
  ],
  images: [
    'https://images.pexels.com/photos/2781760/pexels-photo-2781760.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    'https://images.pexels.com/photos/3862136/pexels-photo-3862136.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    'https://images.pexels.com/photos/1109541/pexels-photo-1109541.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  ],
  applications: [
    'Промышленные предприятия',
    'Торговые и офисные центры',
    'Жилые комплексы',
    'Объекты инфраструктуры',
    'Медицинские учреждения',
    'Дата-центры',
  ],
  documents: [
    { title: 'Технический паспорт', type: 'PDF', size: '1.2 MB' },
    { title: 'Сертификат соответствия', type: 'PDF', size: '0.8 MB' },
    { title: 'Руководство по эксплуатации', type: 'PDF', size: '3.5 MB' },
    { title: 'Габаритный чертеж', type: 'PDF', size: '0.5 MB' },
  ],
  related: [
    { id: 2, name: 'ТСЛ-630/10/0,4', power: '630 кВА' },
    { id: 3, name: 'ТСЛ-1600/10/0,4', power: '1600 кВА' },
    { id: 4, name: 'КТПН-1000/10/0,4', power: '1000 кВА', category: 'ktpn' },
  ],
};

const ProductPage: React.FC = () => {
  const { category } = useParams<{ category?: string; id?: string }>();
  const [activeImage, setActiveImage] = React.useState(0);

  return (
    <>
      <SEO
        title={`${product.name} - ${product.categoryName}`}
        description={`${product.name} от компании Проминнова. ${product.description.substring(
          0,
          160
        )}...`}
        image={product.images[0]}
        product={product}
      />

      {/* Breadcrumbs */}
      <div className="bg-gray-50 py-3">
        <div className="container-custom">
          <div className="flex text-sm">
            <Link to="/" className="text-gray-500 hover:text-primary-600">
              Главная
            </Link>
            <span className="mx-2 text-gray-400">/</span>
            <Link to="/catalog" className="text-gray-500 hover:text-primary-600">
              Каталог
            </Link>
            {category && (
              <>
                <span className="mx-2 text-gray-400">/</span>
                <Link to={`/catalog/${category}`} className="text-gray-500 hover:text-primary-600">
                  {product.categoryName}
                </Link>
              </>
            )}
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-700">{product.name}</span>
          </div>
        </div>
      </div>

      <div className="container-custom py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div>
            <div className="aspect-square mb-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
              <img
                src={product.images[activeImage]}
                alt={product.name}
                className="h-full w-full object-contain p-4"
              />
            </div>

            <div className="grid grid-cols-4 gap-4">
              {product.images.map((image, index) => (
                <button
                  key={index}
                  className={`relative aspect-square rounded-md overflow-hidden border-2 ${activeImage === index ? 'border-primary-500' : 'border-gray-200'}`}
                  onClick={() => setActiveImage(index)}
                >
                  <img
                    src={image}
                    alt={`${product.name} - изображение ${index + 1}`}
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div>
            <h1 className="text-3xl font-bold mb-4">{product.name}</h1>

            <div className="flex items-center space-x-4 mb-6">
              <span className="inline-flex items-center bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full">
                В наличии
              </span>
              <span className="text-gray-500">Артикул: TSL-1000-10-04</span>
            </div>

            <p className="text-gray-700 mb-6">{product.description}</p>

            {/* Key features */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Характеристики</h3>
              <div className="space-y-2">
                {Object.entries(product.features)
                  .slice(0, 6)
                  .map(([key, value]) => (
                    <div key={key} className="flex">
                      <span className="text-gray-600 w-60">{key}:</span>
                      <span className="font-medium">{value}</span>
                    </div>
                  ))}
              </div>
              <button className="text-primary-600 hover:text-primary-700 mt-3 font-medium">
                Все характеристики
              </button>
            </div>

            {/* CTA buttons */}
            <div className="space-y-4 mb-8">
              <button className="w-full btn-primary">
                <FileText className="mr-2 w-5 h-5" />
                Запросить цену
              </button>

              <div className="grid grid-cols-2 gap-4">
                <a href="tel:+79137701139" className="btn-outline">
                  <Phone className="mr-2 w-5 h-5" />
                  Позвонить
                </a>
                <button className="btn-outline">
                  <Share2 className="mr-2 w-5 h-5" />
                  Поделиться
                </button>
              </div>
            </div>

            {/* Documents */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Документация</h3>
              <div className="space-y-2">
                {product.documents.map((doc, index) => (
                  <a
                    key={index}
                    href="#"
                    className="flex items-center p-3 border border-gray-200 rounded-md hover:bg-gray-50"
                  >
                    <Download className="w-5 h-5 text-primary-600 mr-3" />
                    <div className="flex-1">
                      <div className="font-medium">{doc.title}</div>
                      <div className="text-xs text-gray-500">
                        {doc.type}, {doc.size}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Advantages */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold mb-6">Преимущества {product.name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {product.advantages.map((advantage, index) => (
              <div key={index} className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center mb-2">
                  <div className="bg-primary-100 text-primary-600 p-1 rounded-full mr-3">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div className="font-medium">{advantage}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Applications */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold mb-6">Области применения</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {product.applications.map((app, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg p-4 text-center"
              >
                <div className="font-medium">{app}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Technical Specs */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold mb-6">Технические характеристики</h2>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2">
              {Object.entries(product.features).map(([key, value], index) => (
                <div
                  key={key}
                  className={`flex px-6 py-4 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
                >
                  <span className="text-gray-600 w-60">{key}:</span>
                  <span className="font-medium">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Related Products */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold mb-6">Похожие товары</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {product.related.map((item) => (
              <Link
                key={item.id}
                to={`/catalog/${item.category || category}/${item.id}`}
                className="card card-hover group"
              >
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-primary-600 transition-colors">
                    {item.name}
                  </h3>
                  <div className="mb-4">
                    <div className="flex items-center text-gray-600 mb-1">
                      <span className="w-32 text-sm">Мощность:</span>
                      <span className="font-medium">{item.power}</span>
                    </div>
                  </div>
                  <div className="inline-flex items-center font-medium text-primary-600">
                    Подробнее
                    <svg
                      className="ml-2 w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Request Form */}
        <div className="mt-16 bg-primary-50 rounded-xl p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold mb-4">Получить предложение на {product.name}</h2>
              <p className="text-gray-600 mb-6">
                Заполните форму, и наш менеджер свяжется с вами в течение 2 часов (в рабочее время)
                с подробным коммерческим предложением.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="bg-primary-100 text-primary-600 p-1 rounded-full mr-3 mt-1">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p>Актуальная цена с учетом наличия на складе</p>
                </div>
                <div className="flex items-start">
                  <div className="bg-primary-100 text-primary-600 p-1 rounded-full mr-3 mt-1">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p>Расчет сроков доставки до вашего объекта</p>
                </div>
                <div className="flex items-start">
                  <div className="bg-primary-100 text-primary-600 p-1 rounded-full mr-3 mt-1">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p>Подбор дополнительного оборудования при необходимости</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <ContactForm buttonText="Запросить предложение" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductPage;
